<template>
  <div
    class="fixed inset-0 z-[70] w-screen h-screen pt-safe pb-safe bg-[#1E1B2B] flex items-center"
  >
    <div class="flex flex-col items-center w-full gap-8 p-8">
      <div class="relative">
        <span
          v-if="status === 'loading'"
          class="ping-small absolute inset-0 rounded-full bg-v-300/40"
          aria-hidden="true"
        />
        <div
          class="relative flex items-center justify-center w-24 h-24 rounded-full"
          :class="{
            'bg-v-300': status === 'loading',
            'bg-g-500': status === 'success',
            'bg-r-500': status === 'error'
          }"
        >
          <template v-if="status === 'loading'">
            <l-icon name="mobile" size="small" class="text-white scale-[300%]" />
            <div
              class="absolute -bottom-2 -right-2 flex h-10 w-10 items-center justify-center rounded-full bg-g-550/80"
            >
              <l-icon name="card" size="x-small" class="text-white scale-[180%]" />
            </div>
          </template>
          <l-icon
            v-else-if="status === 'success'"
            name="check-circle"
            class="text-white scale-[300%]"
          />
          <l-icon v-else-if="status === 'error'" name="close" class="text-white scale-[300%]" />
        </div>
      </div>
      <div class="font-heading text-2xl font-bold">
        <template v-if="status === 'loading'">
          {{ t('payment-screen.payment-in-process') }}
        </template>
        <template v-else-if="status === 'success'">
          {{ t('payment-screen.payment-approved') }}
        </template>
        <template v-else-if="status === 'error'">
          {{ t('payment-screen.payment-failed') }}
        </template>
      </div>
      <div class="text-4xl font-semibold">{{ formattedAmount }}</div>
      <div class="flex flex-col gap-2 items-center text-center">
        <template v-if="status === 'loading'">
          <div class="text-lg">{{ t('payment-screen.sent-to-terminal') }}</div>
          <div class="text-n-300">{{ t('payment-screen.complete-on-reader') }}</div>
        </template>
        <template v-else-if="status === 'success'">
          <div class="text-n-300">{{ t('payment-screen.transaction-completed') }}</div>
        </template>
        <template v-else-if="status === 'error'">
          <div class="text-n-300">{{ errorMessage || t('payment-screen.transaction-failed') }}</div>
        </template>
      </div>
      <l-button-pos
        v-if="status === 'loading' && cancellable"
        icon="close"
        type="secondary"
        @click="$emit('cancel')"
      >
        {{ t('payment-screen.cancel-operation') }}
      </l-button-pos>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { LIcon } from '@last/core-ui/paprika'

import { useMoney } from '@/composables/useMoney'

import LButtonPos from './LButton.vue'

type PaymentStatus = 'loading' | 'success' | 'error'

const props = withDefaults(
  defineProps<{
    amount?: number
    cancellable?: boolean
    status?: PaymentStatus
    errorMessage?: string
  }>(),
  {
    amount: 0,
    cancellable: false,
    status: 'loading',
    errorMessage: ''
  }
)

defineEmits<{
  cancel: []
}>()

const { currency } = useMoney()
const { t } = useI18n()

const formattedAmount = computed(() => {
  return currency(props.amount ?? 0)
})
</script>

<style scoped>
@keyframes ping-small {
  75%,
  100% {
    transform: scale(1.3);
    opacity: 0;
  }
}
.ping-small {
  animation: ping-small 1s cubic-bezier(0, 0, 0.2, 1) infinite;
}
</style>
